// The installed iPhone app (Home Screen, drawn under the status bar): iOS can give the web view a
// height that stops above the home indicator while still reporting a 34px bottom safe area, which
// left an empty band under the tabs (Axel, 2026-10-06; screenshots: the page ends ~44px above the
// screen's bottom, and painting past it is clipped). When the web view is that short, the bottom
// inset is already outside it: html.ios-short-viewport sets --safe-bottom to 0 (tokens.css), so
// the tabs, sheets and player sit at the page's real bottom edge.

function safeTop(): number {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px)';
  document.body.appendChild(probe);
  const v = Number.parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return v;
}

export function installStandaloneHeight(): void {
  const html = document.documentElement;
  if (!html.classList.contains('is-ios') || !html.classList.contains('is-standalone')) return;
  const set = () => {
    const portrait = window.matchMedia('(orientation: portrait)').matches;
    const screenH = portrait ? Math.max(screen.width, screen.height) : Math.min(screen.width, screen.height);
    // drawn from the screen's top (under the status bar) yet ending well above its bottom
    const short = safeTop() > 0 && screenH - window.innerHeight >= 20;
    html.classList.toggle('ios-short-viewport', short);
  };
  set();
  window.addEventListener('resize', set);
  window.addEventListener('orientationchange', () => window.setTimeout(set, 300));
  window.addEventListener('focusout', unpanAfterKeyboard);
  // the safety net: a view left panned with no field to type in (the dictation of 2026-10-13 kept an
  // editing focus without a keyboard, and a pan put every tap off its target) goes back at once
  window.visualViewport?.addEventListener('scroll', unpanIdle);
  window.visualViewport?.addEventListener('resize', unpanIdle);
  window.addEventListener('pointerup', unpanIdle, { passive: true });
}

/** A field the keyboard is open for (a read-only field, or one asking for no keyboard, is none). */
export function isEditable(el: Element | null): boolean {
  if (!el) return false;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled && el.inputMode !== 'none';
  return (
    el instanceof HTMLInputElement &&
    !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'file', 'color', 'image', 'hidden'].includes(el.type) &&
    !el.readOnly &&
    !el.disabled &&
    el.getAttribute('inputmode') !== 'none'
  );
}

/** The installed app's view is panned (the shell is pinned to the screen: it never scrolls itself). */
function panned(): boolean {
  return (window.visualViewport?.offsetTop ?? 0) > 0 || window.scrollY > 0;
}

/** No field to type in, yet the view is panned: put it back (not while pinch-zoomed in: that pan is the person's). */
export function unpanIdle(): void {
  if (isEditable(document.activeElement)) return;
  if ((window.visualViewport?.scale ?? 1) > 1.01) return;
  if (panned()) window.scrollTo(0, 0);
}

/**
 * iOS pans the whole web view up to keep a focused field above the keyboard, and after the keyboard
 * closes it can leave it panned: the shell (position: fixed, never scrolled itself) then sits too
 * high, with an empty band under the tabs, until the app is reopened. When focus leaves a field and
 * no other field takes it (moving between fields keeps the keyboard), put the view back.
 */
function unpanAfterKeyboard(): void {
  window.setTimeout(() => {
    unpanIdle();
  }, 50);
}
